'use client';

import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import { VariantProps } from 'tailwind-variants';

import { getGTMPromotionSource, useGTMPromotionBanner } from '@/fo/api/gtm';
import { UiAdvertisingInformerBase } from '@/fo/ui/advertising/informer/base';
import {
  UiKitBreadcrumbsBase,
  UiKitBreadcrumbsBaseProps
} from '@/fo/ui/kit/breadcrumbs/base';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitCarouselBase } from '@/fo/ui/kit/carousel/base';
import { UiKitLabelBrand } from '@/fo/ui/kit/label/brand';
import { utilImageLoader } from '@/fo/util/image-loader';

import { eventBannerHero } from '../utils/event';

import { styles } from './UiKitBannerHero.styles';

type Variants = VariantProps<typeof styles>;

export interface Banner {
  image: {
    sm: string;
    md: string;
  };
  alt?: string;
  label?: string;
  labelLink?: string;
  title: React.ReactNode;
  linkPageText?: string;
  subtitle?: string;
  description?: React.ReactNode;
  buttonText: string;
  link: string;
  brandColor?: string;
  mode?: Variants['mode'];
  target?: boolean;
  erid?: string;
  promotionName?: string;
}

export type BannerHeroTitleAs = 'h1' | 'h2' | 'div';

export interface UiKitBannerHeroProps {
  children?: React.ReactNode;
  banners: Banner[];
  breadcrumbs?: UiKitBreadcrumbsBaseProps;
  className?: string;
  type?: Variants['type'];
  mode?: Variants['mode'];
  speed?: number;
  advertisingWarningText?: string;
  titleAs?: BannerHeroTitleAs;
}

type BannerHeroSlideProps = {
  banner: Banner;
  index: number;
  type?: Variants['type'];
  slots: ReturnType<typeof styles>;
  advertisingWarningText?: string;
  sendMultiple: boolean;
  promotionSource: ReturnType<typeof getGTMPromotionSource>;
  titleAs: BannerHeroTitleAs;
};

const getBannerPromotionName = (banner: Banner) => {
  if (banner.promotionName) return banner.promotionName;
  if (banner.alt) return banner.alt;
  if (typeof banner.title === 'string') return banner.title;

  return undefined;
};

function BannerHeroSlide({
  banner,
  index,
  type,
  slots,
  advertisingWarningText,
  sendMultiple,
  promotionSource,
  titleAs: TitleTag
}: BannerHeroSlideProps) {
  const { ref, trackSelectPromotion } = useGTMPromotionBanner({
    creativeName: banner.image.md || banner.image.sm,
    promotionId: banner.link,
    promotionName: getBannerPromotionName(banner),
    source: promotionSource,
    index: index + 1,
    enabled: type !== 'category'
  });

  return (
    <div
      ref={ref}
      className={slots.banner()}
    >
      {banner.erid && (
        <UiAdvertisingInformerBase
          erid={banner.erid || ''}
          advertisingWarningText={advertisingWarningText}
          className={slots.advertisingInformer()}
          sendMultiple={sendMultiple}
        />
      )}
      {(banner.image.md || banner.image.sm) && (
        <picture>
          <source
            srcSet={utilImageLoader({
              src: banner.image.sm,
              width: 375,
              quality: 100
            })}
            media="(max-width: 767px)"
          />
          <Image
            className={slots.image()}
            src={banner.image.md}
            alt={banner.alt || ''}
            width={1440}
            height={360}
            priority={index === 0}
          />
        </picture>
      )}
      <div className={slots.wrapper()}>
        <div className={slots.content()}>
          {type === 'main' && banner.label && (
            <UiKitLabelBrand
              url={banner.labelLink || banner.link}
              color={banner.brandColor}
              className={slots.label()}
            >
              {banner.label}
            </UiKitLabelBrand>
          )}
          <TitleTag className={slots.title()}>{banner.title}</TitleTag>
          {Boolean(banner?.subtitle) && (
            <p className={slots.text()}>{banner.subtitle}</p>
          )}
          {Boolean(banner?.description) && (
            <p className={slots.description()}>{banner.description}</p>
          )}
          {Boolean(
            type !== 'category' && banner.link && banner.linkPageText
          ) && (
            <UiKitButtonBase
              as="link"
              className={slots.button()}
              href={banner.link}
              color={banner.mode === 'dark' ? 'secondary' : 'default'}
              target={banner.target ? '_blank' : '_self'}
              onClick={trackSelectPromotion}
            >
              {banner.linkPageText}
            </UiKitButtonBase>
          )}
          {Boolean(
            type !== 'category' && banner.buttonText && !banner.linkPageText
          ) && (
            <UiKitButtonBase
              as="link"
              className={slots.button()}
              href={banner.link || '/'}
              color={banner.mode === 'dark' ? 'secondary' : 'default'}
              target={banner.target ? '_blank' : '_self'}
              onClick={trackSelectPromotion}
            >
              {banner.buttonText}
            </UiKitButtonBase>
          )}
        </div>
      </div>
    </div>
  );
}

export function UiKitBannerHero({
  banners,
  breadcrumbs,
  className,
  type,
  mode,
  advertisingWarningText,
  titleAs,
  ...props
}: UiKitBannerHeroProps) {
  const TitleTag =
    titleAs ?? (type === 'category' || type === 'actions' ? 'h1' : 'h2');
  const [currentBanner, setCurrentBanner] = useState<Banner | null>(banners[0]);

  const slots = styles({ className, type, mode: currentBanner?.mode || mode });
  const promotionSource = getGTMPromotionSource(banners.length);

  useEffect(() => {
    if (type === 'main') {
      eventBannerHero.setMode(currentBanner?.mode || 'off');
    }
  }, [currentBanner, type]);

  useEffect(() => {
    return () => {
      eventBannerHero.setMode('off');
    };
  }, []);

  return (
    <div
      className={slots.base()}
      {...props}
    >
      <div className={slots.breadcrumbsWrapper()}>
        {!!breadcrumbs && (
          <UiKitBreadcrumbsBase
            items={breadcrumbs.items}
            className={slots.breadcrumbs()}
          />
        )}
      </div>

      <UiKitCarouselBase
        options={{ active: banners.length > 1, watchDrag: true }}
        className={slots.carousel()}
        dotsClass={slots.dots()}
        arrowPrevClass={slots.arrowPrev()}
        arrowNextClass={slots.arrowNext()}
        autoplay={10000}
        showDots
        fade
        onSlidesChange={(activeIndex) => {
          setCurrentBanner(banners[activeIndex]);
        }}
      >
        {banners.map((banner, index) => (
          <BannerHeroSlide
            key={index}
            banner={banner}
            index={index}
            type={type}
            slots={slots}
            advertisingWarningText={advertisingWarningText}
            sendMultiple={banners.length > 1}
            promotionSource={promotionSource}
            titleAs={TitleTag}
          />
        ))}
      </UiKitCarouselBase>
    </div>
  );
}
